import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { colors } from '../theme/colors';
import { lightColors } from '../theme/lightColors';
import { darkTokens, lightTokens, cssVarMap, type ThemeTokens } from '../theme/tokens';

type ThemeMode = 'dark' | 'light' | 'system';
type ResolvedTheme = 'dark' | 'light';

interface ThemeContextType {
  theme: ThemeMode;                    // User preference (dark/light/system)
  resolvedTheme: ResolvedTheme;        // Actual applied theme
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;             // Legacy support - cycles through options
  colors: typeof colors;               // Backwards compat (legacy palette)
  tokens: ThemeTokens;                 // Semantic tokens (new system)
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'didi-theme-preference';

const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'dark';
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch (error) {
    console.warn('[Theme] matchMedia not supported, defaulting to dark:', error);
    return 'dark';
  }
};

// Apply CSS variables to document root from semantic tokens.
// Also keeps legacy `--color-*` vars for backwards-compat with existing CSS.
const applyCssVariables = (tokens: ThemeTokens, legacyColors: typeof colors, resolvedTheme: ResolvedTheme) => {
  const root = document.documentElement;

  root.setAttribute('data-theme', resolvedTheme);

  // New semantic tokens (preferred — use these going forward)
  (Object.keys(cssVarMap) as Array<keyof ThemeTokens>).forEach((key) => {
    root.style.setProperty(cssVarMap[key], tokens[key]);
  });

  // Legacy aliases (kept for backwards compat with code using --color-*)
  root.style.setProperty('--color-background', tokens.bgCanvas);
  root.style.setProperty('--color-surface', tokens.bgSurface);
  root.style.setProperty('--color-surface-elevated', tokens.bgElevated);
  root.style.setProperty('--color-text-primary', tokens.fgPrimary);
  root.style.setProperty('--color-text-secondary', tokens.fgSecondary);
  root.style.setProperty('--color-border', tokens.borderDefault);
  root.style.setProperty('--color-border-light', tokens.borderSubtle);
  root.style.setProperty('--color-primary', legacyColors.deepTrustBlue);
  root.style.setProperty('--color-accent', legacyColors.honestTeal);
  root.style.setProperty('--color-success', legacyColors.truthGreen);
  root.style.setProperty('--color-warning', legacyColors.insightOrange);
  root.style.setProperty('--color-error', legacyColors.cautionRed);
  root.style.setProperty('--gradient-primary', legacyColors.gradients.trustBloom);
  root.style.setProperty('--gradient-secondary', legacyColors.gradients.progressPop);
  root.style.setProperty('--gradient-overlay', legacyColors.gradients.darkOverlay);
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'dark' || stored === 'light' || stored === 'system') {
        return stored;
      }
      if (stored !== null) {
        console.warn('[Theme] Invalid stored value, using default:', stored);
      }
      return 'dark';
    } catch (error) {
      console.warn('[Theme] Failed to read preference from localStorage:', error);
      return 'dark';
    }
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (theme === 'system') {
      return getSystemTheme();
    }
    return theme as ResolvedTheme;
  });

  useEffect(() => {
    if (theme === 'system') {
      setResolvedTheme(getSystemTheme());
    } else {
      setResolvedTheme(theme);
    }
  }, [theme]);

  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  }, [theme]);

  useEffect(() => {
    const tokens = resolvedTheme === 'dark' ? darkTokens : lightTokens;
    const legacyColors = resolvedTheme === 'dark' ? colors : lightColors;
    applyCssVariables(tokens, legacyColors, resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((newTheme: ThemeMode) => {
    setThemeState(newTheme);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => {
      if (prev === 'dark') return 'light';
      if (prev === 'light') return 'system';
      return 'dark';
    });
  }, []);

  const currentColors = resolvedTheme === 'dark' ? colors : lightColors;
  const currentTokens = resolvedTheme === 'dark' ? darkTokens : lightTokens;

  return (
    <ThemeContext.Provider value={{
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
      colors: currentColors,
      tokens: currentTokens,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
